import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const Microneedling = () => {
  const navigate = useNavigate();
  const [currentBenefitIndex, setCurrentBenefitIndex] = useState(0);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        const offset = 90;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const pricingOptions = [
    {
      title: 'Skin Pen Microneedling Basic',
      price: '$299/SESSION (REC 3 SESSIONS)'
    },
    {
      title: 'Skin Pen Microneedling with PRP',
      price: '$399/SESSION (REC 3 SESSIONS)'
    },
    {
      title: 'Skin Pen Microneedling with Salmon DNA',
      price: '$399/SESSION (REC 3 SESSIONS)'
    },
    {
      title: 'Morpheus 8 Microneedling RF',
      price: '$1000/SESSION (REC 3 SESSIONS)'
    }
  ];

  const benefits = [
    {
      title: 'Stimulates Collagen Production',
      description: 'Triggers natural collagen and elastin production for firmer, smoother, more youthful-looking skin.'
    },
    {
      title: 'Reduces Fine Lines and Wrinkles',
      description: 'Minimizes the appearance of fine lines, wrinkles, and crow\'s feet for a more youthful complexion.'
    },
    {
      title: 'Improves Skin Texture',
      description: 'Smooths rough texture, refines pores, and creates a more even skin surface.'
    },
    {
      title: 'Diminishes Acne Scars',
      description: 'Effectively reduces the visibility of acne scars and other types of scarring.'
    },
    {
      title: 'Minimizes Pore Size',
      description: 'Helps reduce enlarged pores for a smoother, more refined skin appearance.'
    },
    {
      title: 'Enhances Product Absorption',
      description: 'Creates micro-channels that allow skincare products to penetrate deeper and work more effectively.'
    },
    {
      title: 'Safe for All Skin Types',
      description: 'Suitable for most skin types and tones with minimal risk of complications.'
    },
    {
      title: 'Minimal Downtime',
      description: 'Quick recovery with only mild redness that typically subsides within 24-48 hours.'
    },
    {
      title: 'Natural Growth Factor Enhancement',
      description: 'When combined with Natural Growth Factors, results are accelerated with improved healing.'
    },
    {
      title: 'Long-Lasting Results',
      description: 'Continued improvements over time as collagen remodeling progresses for sustained benefits.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Microneedling & Morpheus 8 RF in Forest Hills | Century Aesthetics</title>
        <meta name="description" content="SkinPen microneedling and Morpheus 8 RF treatments for scars, wrinkles, and skin texture. Collagen-boosting treatments in Forest Hills, NY." />
        <meta name="keywords" content="microneedling, morpheus 8, skinpen, collagen induction, acne scar treatment, RF microneedling" />
        <link rel="canonical" href="https://www.centuryaesthetics.org/microneedling" />
        
        <meta property="og:title" content="Microneedling & Morpheus 8 RF | Century Longevity & Aesthetics" />
        <meta property="og:description" content="SkinPen microneedling and Morpheus 8 RF treatments for scars, wrinkles, and skin texture. Collagen-boosting treatments in Forest Hills, NY." />
        <meta property="og:url" content="https://www.centuryaesthetics.org/microneedling" />
      </Helmet>

      <div className="min-h-screen bg-white pt-28">
     

      {/* Main Content Section */}
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-lg overflow-hidden shadow-2xl h-[500px]">
                <img 
                  src="/microneedling.jpg" 
                  alt="SkinPen Microneedling Treatment" 
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-6">
                SKINPEN MICRONEEDLING & MORPHEUS 8 RF TREATMENTS FOR SKIN REJUVENATION
              </h2>
              <div className="w-20 h-1 bg-gold mb-6"></div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Microneedling is an advanced skin rejuvenation treatment that stimulates collagen and elastin production through controlled micro-injuries to the skin. Using the FDA-cleared SkinPen device, this minimally invasive procedure effectively treats fine lines, wrinkles, acne scars, enlarged pores, and uneven skin texture. The treatment triggers your body's natural healing response, promoting cellular turnover and revealing smoother, firmer, more youthful-looking skin. Results improve progressively over several months as new collagen continues to form.
                </p>
                <p>
                  We offer multiple microneedling options including basic SkinPen treatments, enhanced protocols with PRP (platelet-rich plasma) or Salmon DNA for accelerated healing, and Morpheus 8 RF microneedling that combines radiofrequency energy with microneedling for deeper skin remodeling. Most patients achieve optimal results with 3 sessions spaced 4-6 weeks apart. Microneedling is safe for all skin types with minimal downtime—expect mild redness for 24-48 hours. Schedule your consultation at Century Longevity & Aesthetics to discover which microneedling treatment is right for you.
                </p>
              </div>

              <motion.button
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="mt-8 bg-olive-darkest text-white px-8 py-4 rounded font-medium text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Now
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 bg-olive-darkest">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-white mb-4">Microneedling Pricing</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {pricingOptions.map((option, index) => (
              <motion.div
                key={option.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg p-10 shadow-xl text-center"
              >
                <h3 className="text-xl font-heading font-semibold text-olive-darkest mb-4 uppercase tracking-wide">
                  {option.title}
                </h3>
                <p className="text-lg font-body text-gold font-semibold mb-6">
                  {option.price}
                </p>
                
                <motion.button
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={scrollToContact}
                  className="w-full bg-olive-darkest text-white px-6 py-3 rounded-lg font-medium text-sm tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg"
                >
                  Book Now
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-4">Benefits of Microneedling (With and Without Natural Growth Factors)</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {/* Carousel Container */}
            <div className="relative">
              {/* Previous Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === 0 ? benefits.length - 1 : prev - 1))}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Previous benefit"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              {/* Next Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === benefits.length - 1 ? 0 : prev + 1))}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Next benefit"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Carousel Card */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentBenefitIndex}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-lg shadow-xl p-8 sm:p-12 min-h-[300px] flex flex-col justify-center"
                >
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <CheckCircle className="w-16 h-16 text-gold" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-3xl font-semibold text-olive-darkest mb-4">
                        {benefits[currentBenefitIndex].title}
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        {benefits[currentBenefitIndex].description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots Indicator */}
              <div className="flex justify-center gap-2 mt-8">
                {benefits.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentBenefitIndex(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === currentBenefitIndex 
                        ? 'w-8 bg-gold' 
                        : 'w-2 bg-gray-300 hover:bg-gold/50'
                    }`}
                    aria-label={`Go to benefit ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16 bg-olive-darkest text-white p-8 sm:p-10 md:p-12 rounded-xl shadow-2xl text-center"
          >
            <p className="text-base sm:text-lg leading-relaxed mb-8 text-white">
              Transform your skin with advanced microneedling treatments. Reduce scars, wrinkles, and texture for a radiant, youthful complexion!
            </p>
            <motion.button
              whileHover={{ y: -2, scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={scrollToContact}
              className="w-full sm:w-auto bg-gold text-olive-darkest px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation mb-10"
            >
              Schedule Consultation
            </motion.button>

            <div className="w-24 h-0.5 bg-gold/30 mx-auto mb-8"></div>

            <h3 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold mb-4 text-white">Have Questions?</h3>
            <p className="text-base sm:text-lg text-white mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
              Learn about microneedling treatments, what to expect during your session, and which option is best for your skin concerns.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  navigate('/faq');
                  setTimeout(() => {
                    const element = document.getElementById('microneedling');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-white px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                View FAQs
              </motion.button>
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-gold px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                Contact Us Directly
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Microneedling;